import { useEffect, useContext, useState } from "react";
import { API, Auth, Hub, PubSub } from 'aws-amplify';
import AWS from "aws-sdk/global";
import Iot from "aws-sdk/clients/iot";
import awsconfig from "./aws-exports";
import { getResponseData } from "./utils/apiHelpers";
import { safeParseLocalStorage } from "./utils/safeParseLocalStorage";

import {
  isPushNotificationSupported,
  askUserPermission,
} from "./push-notifications";

import { connect } from "react-redux";
import * as actions from "./store/actions";

import Toast from "./components/Toast/Toast";

import { SeisoenContext } from "./context/seisoen-context";
import { ProfielContext } from "./context/profiel-context";
import { BoerderyContext } from "./context/boerdery-context";

import { Routes, BrowserRouter, Route } from "react-router-dom";

import ProtectedRoute from "./ProtectedRoute";
import RequireAuth from "./hoc/Layout/RequireAuth/RequireAuth";

import MinimalLayout from "./hoc/Layout/Minimal/Minimal";
import MainLayout from "./hoc/Layout/Main/Main";
import EmptyLayout from "./hoc/Layout/Empty/Empty";

import WaitingPage from "./views/LandingPage/WaitingPage";
import ErrorBoundaryPage from "./views/NotFound/ErrorBoundary";
import { useTranslation } from "react-i18next";
import lazyWithRetry from "./utils/lazyWithRetry";
const NotFoundView = lazyWithRetry(() => import("./views/NotFound/NotFound"));
const SkepEersteBoerdery = lazyWithRetry(() => import("./views/LandingPage/SkepEersteBoerdery/SkepEersteBoerdery"));
const LandingPage = lazyWithRetry(() => import("./views/LandingPage/LandingPage"));

const BoerderyManagement = lazyWithRetry(() => import("./views/AccountManagement/BoerderyManagement/BoerderyManagement"));
const BriefhoofBladsy = lazyWithRetry(() => import("./views/AccountManagement/BoerderyManagement/Briefhoof/Briefhoof"));
const SkepBoerdery = lazyWithRetry(() => import("./views/AccountManagement/BoerderyManagement/SkepBoerdery/SkepBoerdery"));

const PlukstaatDashboard = lazyWithRetry(() => import("./views/PlukstaatDashboard/PlukstaatDashboard"));
const SkepPlukstaat = lazyWithRetry(() => import("./views/PlukstaatDashboard/SkepPlukstaat/SkepPlukstaat"));
const WysigPlukstaat = lazyWithRetry(() => import("./views/PlukstaatDashboard/WysigPlukstaat/WysigPlukstaat"));
const PlukstaatInvoer = lazyWithRetry(() => import("./views/PlukstaatDashboard/PlukstaatInvoer/PlukstaatInvoer"));
const PlukstaatUitdruk = lazyWithRetry(() => import("./views/PlukstaatDashboard/PlukstaatUitdruk/PlukstaatUitdruk"));
const PlukstaatKoppelings = lazyWithRetry(() => import("./views/PlukstaatDashboard/PlukstaatKoppelings/PlukstaatKoppelings"));
const SienPlukstaatRekords = lazyWithRetry(() => import("./views/PlukstaatDashboard/SienRekords/SienRekords"));
const BinsManagement = lazyWithRetry(() => import("./views/PlukstaatDashboard/BinsManagement/BinsManagement"));
const SakkeManagement = lazyWithRetry(() => import("./views/PlukstaatDashboard/SakkeOorskuif/SakkeOorskuif"));
const UitstaandePlukstate = lazyWithRetry(() => import("./views/PlukstaatDashboard/Uitgetekende/Uitgeteken"));
const PlukPage = lazyWithRetry(() => import("./views/Pluk/Dashboard"));

const WerknemersDashboard = lazyWithRetry(() => import("./views/WerknemersDashboard/WerknemersDashboard"));
const WerknemerDashboard = lazyWithRetry(() => import("./views/Werknemers/Dashboard/Dashboard"));
const SkepWerknemer = lazyWithRetry(() => import("./views/WerknemersDashboard/SkepWerknemer/SkepWerknemer"));

const SpanneDashboard = lazyWithRetry(() => import("./views/SpanneDashboard/SpanneDashboard"));
const SkepSpan = lazyWithRetry(() => import("./views/SpanneDashboard/SkepSpan/SkepSpan"));
const WysigSpan = lazyWithRetry(() => import("./views/SpanneDashboard/WysigSpan/WysigSpan"));
const ViewSpan = lazyWithRetry(() => import("./views/SpanneDashboard/ViewSpan/ViewSpan"));

const AktiwiteitSpanneDashboard = lazyWithRetry(() => import("./views/Spanne/SpanneDashboard"));
const AktiwiteitSkepSpan = lazyWithRetry(() => import("./views/Spanne/SkepSpan/SkepSpan"));
const AktiwiteitWysigSpan = lazyWithRetry(() => import("./views/Spanne/WysigSpan/WysigSpan"));
const AktiwiteitViewSpan = lazyWithRetry(() => import("./views/Spanne/ViewSpan/ViewSpan"));

const Plukprestasie = lazyWithRetry(() => import("./views/Rapporteering/Plukprestasie/Plukprestasie"));
const PlukprestasieVerslag = lazyWithRetry(() => import("./views/Rapporteering/Plukprestasie/Verslag/Verslag"));

const Lone = lazyWithRetry(() => import("./views/Finansiel/LoonBetalings/Lone"));

const FinansielDashboard = lazyWithRetry(() => import("./views/Finansiel/Dashboard/FinansielDashboard"));
const IntervertakkingJoernale = lazyWithRetry(() => import("./views/Finansiel/IntervertakkingJoernale/IntervertakkingJoernale"));
const KontrakteurJoernale = lazyWithRetry(() => import("./views/Finansiel/Uitkontraktering/Uitkontraktering"));

const Produksie = lazyWithRetry(() => import("./views/Produksie/Produksie"));
const AnderSkattings = lazyWithRetry(() => import("./views/Produksie/AnderSkattings/AnderSkattings"));

const Voertuie = lazyWithRetry(() => import("./views/Voertuie/Voertuie"));
const VoertuieSkep = lazyWithRetry(() => import("./views/Voertuie/Skep/Skep"));
const VoertuieWysig = lazyWithRetry(() => import("./views/Voertuie/Wysig/Wysig"));
const Voertuigtipes = lazyWithRetry(() => import("./views/Voertuigtipes/Voertuigtipes"));
const VoertuigtipeSkep = lazyWithRetry(() => import("./views/Voertuigtipes/Skep/Skep"));
const VoertuigtipeWysig = lazyWithRetry(() => import("./views/Voertuigtipes/Wysig/Wysig"));

const Toerusting = lazyWithRetry(() => import("./views/Toerusting/Toerusting"));
const ToerustingSkep = lazyWithRetry(() => import("./views/Toerusting/Skep/Skep"));
const ToerustingWysig = lazyWithRetry(() => import("./views/Toerusting/Wysig/Wysig"));
const Toerustingtipes = lazyWithRetry(() => import("./views/Toerustingtipes/Toerustingtipes"));
const ToerustingtipeSkep = lazyWithRetry(() => import("./views/Toerustingtipes/Skep/Skep"));
const ToerustingtipeWysig = lazyWithRetry(() => import("./views/Toerustingtipes/Wysig/Wysig"));

const HuurItems = lazyWithRetry(() => import("./views/HuurItems/HuurItems"));
const HuurItemsSkep = lazyWithRetry(() => import("./views/HuurItems/Skep/Skep"));
const HuurItemsWysig = lazyWithRetry(() => import("./views/HuurItems/Wysig/Wysig"));

const Verbruiksitem = lazyWithRetry(() => import("./views/Verbruiksitems/Verbruiksitems"));
const VerbruiksitemSkep = lazyWithRetry(() => import("./views/Verbruiksitems/Skep/Skep"));
const VerbruiksitemWysig = lazyWithRetry(() => import("./views/Verbruiksitems/Wysig/Wysig"));
const Verbruiksitemtipes = lazyWithRetry(() => import("./views/Verbruiksitemtipes/Verbruiksitemtipes"));
const VerbruiksitemtipeSkep = lazyWithRetry(() => import("./views/Verbruiksitemtipes/Skep/Skep"));
const VerbruiksitemtipeWysig = lazyWithRetry(() => import("./views/Verbruiksitemtipes/Wysig/Wysig"));

const OpstellingsDashboard = lazyWithRetry(() => import("./views/Opstellings/OpstellingsDashboard"));
const Seisoene = lazyWithRetry(() => import("./views/Seisoene/Seisoene"));
const SkepSeisoen = lazyWithRetry(() => import("./views/Seisoene/Skep/Skep"));
const WysigSeisoen = lazyWithRetry(() => import("./views/Seisoene/Wysig/Wysig"));
const Gewasse = lazyWithRetry(() => import("./views/Gewasse/Gewasse"));
const SkepGewas = lazyWithRetry(() => import("./views/Gewasse/Skep/Skep"));
const WysigGewas = lazyWithRetry(() => import("./views/Gewasse/Wysig/Wysig"));
const Kultivars = lazyWithRetry(() => import("./views/Kultivars/Kultivars"));
const SkepKultivar = lazyWithRetry(() => import("./views/Kultivars/Skep/Skep"));
const WysigKultivar = lazyWithRetry(() => import("./views/Kultivars/Wysig/Wysig"));
const Maatskappye = lazyWithRetry(() => import("./views/Maatskappye/Maatskappye"));
const SkepMaatskappy = lazyWithRetry(() => import("./views/Maatskappye/Skep/Skep"));
const WysigMaatskappy = lazyWithRetry(() => import("./views/Maatskappye/Wysig/Wysig"));

const TeenwoordigheidDashboard = lazyWithRetry(() => import("./views/Teenwoordigheid/Teenwoordigheid"));
const TeenwoordigheidVerslag = lazyWithRetry(() => import("./views/Teenwoordigheid/Verslag/Verslag"));
const TydregisterDashboard = lazyWithRetry(() => import("./views/Tydregister/Tydregister"));
const TydregisterVerslag = lazyWithRetry(() => import("./views/Tydregister/Verslag/Verslag"));

const Plukverslae = lazyWithRetry(() => import("./views/Pluk/Verslae/Verslae"));

const Profiel = lazyWithRetry(() => import("./views/UserProfile/Dashboard/Dashboard"));

const Rolle = lazyWithRetry(() => import("./views/Rolle/Rolle"));
const SkepRolBladsy = lazyWithRetry(() => import("./views/Rolle/SkepRol/SkepRol"));
const SienRol = lazyWithRetry(() => import("./views/Rolle/LeesRol/LeesRol"));
const WysigRol = lazyWithRetry(() => import("./views/Rolle/WysigRol/WysigRol"));

const TransaksieLogs = lazyWithRetry(() => import("./views/TransaksieLogs/TransaksieLogs"));
const Subscriptions = lazyWithRetry(() => import("./views/Subscriptions/Subscriptions"));
const SkepSubskripsie = lazyWithRetry(() => import("./views/Subscriptions/Skep/Skep"));
const SienSubskripsie = lazyWithRetry(() => import("./views/Subscriptions/Sien/Sien"));

const AktiwiteiteDashboard = lazyWithRetry(() => import("./views/Aktiwiteite/Aktiwiteite"));

const Dashboard = lazyWithRetry(() => import("./views/DashboardV2/Dashboard"));

const Vaardighede = lazyWithRetry(() => import("./views/Vaardighede/Vaardighede"));
const SkepVaardigheid = lazyWithRetry(() => import("./views/Vaardighede/Skep/Skep"));
const WysigVaardigheid = lazyWithRetry(() => import("./views/Vaardighede/Wysig/Wysig"));
const ViewVaardigheid = lazyWithRetry(() => import("./views/Vaardighede/View/View"));

const Modules = lazyWithRetry(() => import("./views/Modules/Modules"));
const ModuleInfo = lazyWithRetry(() => import("./views/Modules/Info"));

const Dienste = lazyWithRetry(() => import("./views/Dienste/Dienste"));
const DiensteInfo = lazyWithRetry(() => import("./views/Dienste/Info"));

const BetaalmetodesPage = lazyWithRetry(() => import("./views/Betaalmetodes/Betaalmetodes"));
const BetaalmetodesSetup = lazyWithRetry(() => import("./views/Betaalmetodes/Setup/Setup"));

const Klokreels = lazyWithRetry(() => import("./views/Klokreels/Klokreels"));
const KlokreelsSetup = lazyWithRetry(() => import("./views/Klokreels/Setup/Setup"));

const AlgemeneOpstellingsDashboard = lazyWithRetry(() => import("./views/Pluk/AlgemeneOpstellings/Dashboard/Dashboard"));

const DatabronneDashboard = lazyWithRetry(() => import("./views/Databronne/Databronne"));
const DatabronneSkep = lazyWithRetry(() => import("./views/Databronne/Skep/Skep"));
const DatabronneWysig = lazyWithRetry(() => import("./views/Databronne/Wysig/Wysig"));
const DatabronneDupliseer = lazyWithRetry(() => import("./views/Databronne/Dupliseer/Dupliseer"));

// import QuicksightDashboard from "./views/QuicksightDashboard/QuicksightDashboard";

const Plukbeplanner = lazyWithRetry(() => import("./views/Pluk/Beplanner/Beplanner"));
const Insidente = lazyWithRetry(() => import("./views/Insidente/Insidente"));
const InsidenteKategorie = lazyWithRetry(() => import("./views/Insidente/Insidentekategorie/Insidentekategorie"));
const InsidenteKategorieSkep = lazyWithRetry(() => import("./views/Insidente/Insidentekategorie/Skep/Skep"));
const InsidenteKategorieWysig = lazyWithRetry(() => import("./views/Insidente/Insidentekategorie/Wysig/Wysig"));

const OperationeelDashboard = lazyWithRetry(() => import("./views/Operationeel/OperationeelDashboard"));
const QRKodes = lazyWithRetry(() => import("./views/QRKodes/QRKodes"));

const IntegrasiesDashboard = lazyWithRetry(() => import("./views/Integrasies/IntegrasiesDashboard"));
const PayrollIntegrasieDashboard = lazyWithRetry(() => import("./views/Integrasies/Payroll/Payroll"));
const PayrollIntegrasieSkep = lazyWithRetry(() => import("./views/Integrasies/Payroll/Skep/Skep"));
const PayrollIntegrasieWysig = lazyWithRetry(() => import("./views/Integrasies/Payroll/Wysig/Wysig"));
const Verlofreels = lazyWithRetry(() => import("./views/KlokOpstellings/Verlof/Verlof"));
const Klokopstellings = lazyWithRetry(() => import("./views/KlokOpstellings/Dashboard"));
const SkepVerlof = lazyWithRetry(() => import("./views/KlokOpstellings/Verlof/SkepWysig/Skep"));
const WysigVerlof = lazyWithRetry(() => import("./views/KlokOpstellings/Verlof/SkepWysig/Wysig"));
const TVTreels = lazyWithRetry(() => import("./views/KlokOpstellings/TVT/TVT"));
const SkepTvt = lazyWithRetry(() => import("./views/KlokOpstellings/TVT/SkepWysig/Skep"));
const WysigTvt = lazyWithRetry(() => import("./views/KlokOpstellings/TVT/SkepWysig/Wysig"));

const RekeningDashboard = lazyWithRetry(() => import("./views/Rekening/RekeningDashboard"));
const RekeningVerstellings = lazyWithRetry(() => import("./views/Rekening/BillingSettings/BillingSettings"));
const VerbruikDashboard = lazyWithRetry(() => import("./views/Rekening/Verbruik/Verbruik"));

const Rekeninge = lazyWithRetry(() => import("./views/Rekening/Rekeninge/Rekeninge"));

const RoutesComponent = (props) => {
  const { i18n } = useTranslation();

  const boerderyContext = useContext(BoerderyContext);
  const seisoenContext = useContext(SeisoenContext);
  const profielContext = useContext(ProfielContext);

  const [shouldLoad, setShouldLoad] = useState(true);
  const [userConsent, setUserConsent] = useState();

  const [nieBoerderye, setNieBoerderye] = useState([]);

  useEffect(() => {
    if (isPushNotificationSupported()) {
      if (userConsent !== "granted") {
        askUserPermission();
      } else {
        setUserConsent(Notification.permission);
      }
    }
    Hub.listen("auth", (data) => {
      if (data.payload.event === "signIn" || data.payload.event === "autoSignIn") {
        Auth.currentCredentials().then((info) => {
          const cognitoIdentityId = info.identityId;
          if (info.authenticated) {
            AWS.config.update({
              region: awsconfig.aws_cognito_region,
              credentials: Auth.essentialCredentials(info),
            });
            var params = {
              policyName: "bsPolicy",
              principal: cognitoIdentityId,
            };
            const iot = new Iot({ apiVersion: "2015-05-28" });
            iot.attachPrincipalPolicy(params, (err, data) => {
              if (err) {
                console.log(err, err.stack);
              }
            });
          }
        });
        setShouldLoad(!shouldLoad);
      }
    });
    return () => {
      Hub.remove("auth");
    };
  }, []);

  const subscribeTopic = () => {
    PubSub.subscribe([
      `BS/${boerderyContext.boerdery.boerderyuuid}`,
      `BS/${boerderyContext.boerdery.boerderyuuid}/${profielContext.profiel.gebruikernaam}`,
    ]).subscribe({
      next: (data) => {
        // console.log("[PubSub] Message received", data);
        props.addNotification(data.value);
      },
      error: (error) => {
        console.error(error);
        // setTimeout(() => {
        //   Amplify.addPluggable(
        //     new AWSIoTProvider({
        //       aws_pubsub_region: process.env.REACT_APP_AWS_PUBSUB_REGION,
        //       aws_pubsub_endpoint: process.env.REACT_APP_AWS_PUBSUB_ENDPOINT,
        //     })
        //   );
        //   subscribeTopic();
        // }, 1000 * 10);
      },
      close: () => console.log("Done"),
    });
  };

  useEffect(() => {
    if (
      boerderyContext.boerdery &&
      boerderyContext.boerdery.boerderyuuid &&
      profielContext.profiel.gebruikernaam
    ) {
      subscribeTopic();
    }
    return () => { };
  }, [boerderyContext.boerdery, profielContext.profiel.gebruikernaam]);

  const getBoodskappe = async () => {
    let apiName = "AWSApiGateway";
    let path = `/boodskappe`;
    return await API.get(apiName, path, { response: true })
      .then((response) => {
        if (response.status == 204) {
          return [];
        } else {
          return getResponseData(response, []);
        }
      })
      .catch((error) => {
        throw JSON.stringify(error.response);
      });
  };

  const getSeisoene = async () => {
    let apiName = "AWSApiGateway";
    let path = `/seisoene`;
    return await API.get(apiName, path, { response: true })
      .then((response) => {
        if (response.status == 204) {
          return [];
        } else {
          return getResponseData(response, []);
        }
      })
      .catch((error) => {
        throw JSON.stringify(error.response);
      });
  };

  const getProfiel = async () => {
    return Auth.currentAuthenticatedUser({
      bypassCache: false, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
    })
      .then((user) => {
        let profiel = {
          sub: user.attributes.sub,
          gebruikernaam: user.username,
          naam: user.attributes.name,
          van: user.attributes.family_name,
          epos: user.attributes.email,
          selfoon: user.attributes.phone_number.replace(/-|\+|\s/g, ""),
        };
        i18n.changeLanguage(user.attributes["custom:language"]);
        return profiel;
      })
      .catch((error) => {
        console.log(error);
        throw JSON.stringify(error);
      });
  };

  const getBoerderye = async () => {
    let apiName = "AWSApiGateway";
    let path = `/boerderye`;
    return await API.get(apiName, path, { response: true })
      .then((response) => {
        if (response.status == 204) {
          return [];
        } else {
          return getResponseData(response, []);
        }
      })
      .catch((error) => {
        console.log(error.response);
        throw JSON.stringify(error.response);
      });
  };

  const getAktiwiteiteToelatings = async (gebruikernaam) => {
    let apiName = "API_Aktiwiteite";
    let path = `/toelatings?gebruikernaam=${gebruikernaam}&module=aktiwiteite`;
    return await API.get(apiName, path, { response: true })
      .then((response) => {
        if (response.status == 204) {
          return [];
        } else {
          return getResponseData(response, []);
        }
      })
      .catch((error) => {
        throw JSON.stringify(error.response);
      });
  };

  const getModules = async () => {
    let apiName = "AWSApiGateway";
    let path = `/modules?aktief=true`;
    return await API.get(apiName, path, { response: true })
      .then((response) => {
        if (response.status == 204) {
          return [];
        } else {
          return getResponseData(response, []).map((item) => item.module);
        }
      })
      .catch((error) => {
        // console.log(error);
        throw JSON.stringify(error.response);
      });
  };

  useEffect(() => {
    const myPromises = () => Promise.all([getBoerderye(), getProfiel()]);
    myPromises()
      .then((results) => {
        // console.log(results);
        if (results[1].sub != localStorage.getItem("sub")) {
          localStorage.removeItem("boerdery");
          localStorage.removeItem("seisoene");
          localStorage.removeItem("seisoen");
          localStorage.removeItem("kliente");
          localStorage.removeItem("klient");
          localStorage.setItem("sub", results[1].sub);
        }
        let boerderye = results[0].filter((item) => !item.spuuid);
        setNieBoerderye(results[0].filter((item) => item.spuuid));

        let kiesHierdieBoerdery = boerderye.length > 0 ? boerderye[0] : {};
        boerderyContext.stelboerderye(boerderye);
        if (
          !localStorage.getItem("boerdery") ||
          !safeParseLocalStorage("boerdery", {}).boerderyuuid
        ) {
          kiesHierdieBoerdery = boerderye.length > 0 ? boerderye[0] : {};
        } else {
          if (safeParseLocalStorage("boerdery", {}).boerderyuuid) {
            kiesHierdieBoerdery =
              boerderye[
              boerderye
                .map((item) => item.boerderyuuid)
                .indexOf(
                  safeParseLocalStorage("boerdery", {}).boerderyuuid
                )
              ];
          } else {
            kiesHierdieBoerdery = boerderye[0];
          }
        }
        boerderyContext.stelboerdery(kiesHierdieBoerdery);
        profielContext.stelprofiel({
          bittoelatings: kiesHierdieBoerdery.werknemer
            ? kiesHierdieBoerdery.werknemer.bittoelatings
            : {},
          rolle: kiesHierdieBoerdery.werknemer
            ? kiesHierdieBoerdery.werknemer.rolle
            : [],
          werknemerid: kiesHierdieBoerdery.werknemer
            ? kiesHierdieBoerdery.werknemer.id
            : null,
          ...results[1],
        });
        boerderyContext.stelrefresh(!boerderyContext.refreshpage);

        if (boerderye.length > 0) {
          getSeisoene()
            .then((response) => {
              // console.log(response);
              seisoenContext.stelseisoene(response);
              if (
                !localStorage.getItem("seisoen") ||
                !safeParseLocalStorage("seisoen", {}).naam
              ) {
                seisoenContext.stelseisoen(
                  response.length > 0 ? response[response.length - 1] : {}
                );
              }
            })
            .catch((error) => {
              console.error(error);
            });
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    if (
      boerderyContext.boerdery &&
      boerderyContext.boerdery.boerderyuuid &&
      profielContext.profiel.gebruikernaam
    ) {
      if (boerderyContext.modules.indexOf("aktiwiteite") !== -1) {
        getAktiwiteiteToelatings(profielContext.profiel.gebruikernaam)
          .then((response) => {
            if (response[0]) {
              profielContext.stelprofiel({
                ...profielContext.profiel,
                modulestoelatings: response,
              });
            }
          })
          .catch((error) => {
            profielContext.stelprofiel({
              ...profielContext.profiel,
              modulestoelatings: null,
            });
            console.error(error);
          });
      } else {
        profielContext.stelprofiel({
          ...profielContext.profiel,
          modulestoelatings: null,
        });
      }
    }
  }, [boerderyContext.modules]);

  useEffect(() => {
    if (boerderyContext.reload > 0) {
      getBoerderye()
        .then((results) => {
          let boerderye = results[0].filter((item) => !item.spuuid);
          setNieBoerderye(results[0].filter((item) => item.spuuid));
          if (
            !localStorage.getItem("boerdery") ||
            !safeParseLocalStorage("boerdery", {}).boerderyuuid
          ) {
            let kiesHierdieBoerdery = boerderye.length > 0 ? boerderye[0] : {};
            boerderyContext.stelboerdery(kiesHierdieBoerdery);
            getSeisoene()
              .then((response) => {
                seisoenContext.stelseisoene(response);
                seisoenContext.stelseisoen(
                  response.length > 0 ? response[response.length - 1] : {}
                );
              })
              .catch((error) => {
                console.error(error);
              });
          }
          boerderyContext.stelboerderye(boerderye);
        })
        .catch((error) => {
          console.error(error);
        });
    }
    return () => { };
  }, [boerderyContext.reload]);

  useEffect(() => {
    if (
      boerderyContext.boerdery &&
      boerderyContext.boerdery.boerderyuuid &&
      profielContext.profiel.gebruikernaam
    ) {
      getModules()
        .then((response) => {
          boerderyContext.stelmodules(response);
        })
        .catch((error) => {
          boerderyContext.stelmodules([]);
          profielContext.stelprofiel({
            ...profielContext.profiel,
            modulestoelatings: null,
          });
          console.error(error);
        });
    }
    return () => { };
  }, [boerderyContext.refreshpage]);

  useEffect(() => {
    if (boerderyContext.boerdery && boerderyContext.boerdery.boerderyuuid) {
      props.getNotifications();

      let kliente = nieBoerderye.filter(
        (item) => item.spuuid === boerderyContext.boerdery.boerderyuuid
      );
      if (kliente.length > 0) {
        kliente = [...kliente, boerderyContext.boerdery];
        kliente = kliente.map((item) => {
          item.spuuid = item.spuuid || item.boerderyuuid;
          return item;
        });
        boerderyContext.stelkliente(kliente);
        let kiesHierdieKlient;
        if (
          !localStorage.getItem("klient") ||
          !safeParseLocalStorage("klient", {}).boerderyuuid
        ) {
          let i = kliente
            .map((item) => item.boerderyuuid)
            .indexOf(boerderyContext.boerdery.boerderyuuid);
          if (i === -1) {
            kiesHierdieKlient = kliente[0];
          } else {
            kiesHierdieKlient = kliente[i];
          }
        } else {
          if (safeParseLocalStorage("klient", {}).boerderyuuid) {
            kiesHierdieKlient =
              kliente[
              kliente
                .map((item) => item.boerderyuuid)
                .indexOf(
                  safeParseLocalStorage("klient", {}).boerderyuuid
                )
              ];
          } else {
            kiesHierdieKlient = kliente[0];
          }
        }
        boerderyContext.stelklient(kiesHierdieKlient);
        if (
          kiesHierdieKlient.boerderyuuid !==
          boerderyContext.boerdery.boerderyuuid
        ) {
          profielContext.stelprofiel({
            ...profielContext.profiel,
            bittoelatings:
              kiesHierdieKlient.werknemer &&
                kiesHierdieKlient.werknemer.bittoelatings
                ? kiesHierdieKlient.werknemer.bittoelatings
                : {},
            rolle:
              kiesHierdieKlient.werknemer && kiesHierdieKlient.werknemer.rolle
                ? kiesHierdieKlient.werknemer.rolle
                : [],
            werknemerid:
              kiesHierdieKlient.werknemer && kiesHierdieKlient.werknemer.id
                ? kiesHierdieKlient.werknemer.id
                : null,
          });
        } else {
          profielContext.stelprofiel({
            ...profielContext.profiel,
            bittoelatings: boerderyContext.boerdery.werknemer
              ? boerderyContext.boerdery.werknemer.bittoelatings
              : {},
            rolle: boerderyContext.boerdery.werknemer
              ? boerderyContext.boerdery.werknemer.rolle
              : [],
            werknemerid: boerderyContext.boerdery.werknemer
              ? boerderyContext.boerdery.werknemer.id
              : null,
          });
          boerderyContext.stelrefresh(!boerderyContext.refreshpage);
        }
      } else {
        boerderyContext.stelrefresh(!boerderyContext.refreshpage);
      }
    }
    return () => { };
  }, [boerderyContext.boerdery]);

  useEffect(() => {
    if (boerderyContext.klient && boerderyContext.klient.boerderyuuid) {
      if (
        boerderyContext.klient.boerderyuuid !==
        boerderyContext.boerdery.boerderyuuid
      ) {
        profielContext.stelprofiel({
          ...profielContext.profiel,
          bittoelatings:
            boerderyContext.klient.werknemer &&
              boerderyContext.klient.werknemer.bittoelatings
              ? boerderyContext.klient.werknemer.bittoelatings
              : {},
          rolle:
            boerderyContext.klient.werknemer &&
              boerderyContext.klient.werknemer.rolle
              ? boerderyContext.klient.werknemer.rolle
              : [],
          werknemerid:
            boerderyContext.klient.werknemer &&
              boerderyContext.klient.werknemer.id
              ? boerderyContext.klient.werknemer.id
              : null,
        });
      } else {
        profielContext.stelprofiel({
          ...profielContext.profiel,
          bittoelatings: boerderyContext.boerdery.werknemer
            ? boerderyContext.boerdery.werknemer.bittoelatings
            : {},
          rolle: boerderyContext.boerdery.werknemer
            ? boerderyContext.boerdery.werknemer.rolle
            : [],
          werknemerid: boerderyContext.boerdery.werknemer
            ? boerderyContext.boerdery.werknemer.id
            : null,
        });
      }
      boerderyContext.stelrefresh(!boerderyContext.refreshpage);
    }
  }, [boerderyContext.klient]);

  useEffect(() => {
    if (seisoenContext.reload > 0) {
      getSeisoene()
        .then((results) => {
          seisoenContext.stelseisoene(results);
          if (
            !localStorage.getItem("seisoen") ||
            !safeParseLocalStorage("seisoen", {}).naam
          ) {
            seisoenContext.stelseisoen(
              results.length > 0 ? results[results.length - 1] : {}
            );
          }
          for (let item of results) {
            // Re-read inside the loop: the block above may have just written a
            // new "seisoen" to localStorage (stelseisoen persists synchronously).
            if (
              item.jaar == safeParseLocalStorage("seisoen", {}).jaar ||
              item.jaar == seisoenContext.seisoen?.jaar ||
              results.length === 1
            ) {
              seisoenContext.stelseisoen(item);
              break;
            }
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
    return () => { };
  }, [seisoenContext.reload]);

  return (
    <ErrorBoundaryPage>
      <BrowserRouter>
        <Toast />
        <Routes>
          <Route element={<RequireAuth />}>

            <Route
              path="/"
              element={
                <WaitingPage />
              }
            />
            <Route
              path="/loading"
              element={
                <WaitingPage />
              }
            />
            <Route
              path="/not-found"
              element={
                <MinimalLayout>
                  <NotFoundView />
                </MinimalLayout>
              }
            />
            <Route
              path="/errorpage"
              element={
                <MainLayout>
                  <ErrorBoundaryPage />
                </MainLayout>
              }
            />

            <Route
              path="/landingpage"
              element={
                <MinimalLayout>
                  <LandingPage />
                </MinimalLayout>
              }
            />
            <Route
              path="/skep-eerste-boerdery"
              element={
                <MinimalLayout>
                  <SkepEersteBoerdery />
                </MinimalLayout>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredPermissions={["dashboard:visit"]}>
                  <MainLayout>
                    <Dashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/:plaas"
              element={
                <ProtectedRoute requiredPermissions={["dashboard:visit"]}>
                  <MainLayout>
                    <Dashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/:plaas/:blok"
              element={
                <ProtectedRoute requiredPermissions={["dashboard:visit"]}>
                  <MainLayout>
                    <Dashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/opstellings"
              element={
                <ProtectedRoute
                  anyPermissions={[
                    "werknemers:visit",
                    "spanne:visit",
                    "voertuie:visit",
                    "seisoene:visit",
                    "gewasse:visit",
                    "kultivars:visit",
                    "maatskappye:visit",
                    "boerderye:visit",
                  ]}
                >
                  <MainLayout>
                    <OpstellingsDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/werknemers"
              element={
                <ProtectedRoute requiredPermissions={["werknemers:visit"]}>
                  <MainLayout>
                    <WerknemersDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route path="werknemers">
              <Route
                path="skep"
                element={
                  <ProtectedRoute requiredPermissions={["werknemers:create"]}>
                    <MainLayout>
                      <SkepWerknemer />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path=":werknemerId"
              >
                <Route path="*"
                  element={
                    <ProtectedRoute requiredPermissions={["werknemers:read"]}>
                      <MainLayout>
                        <WerknemerDashboard />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Route>

            <Route
              path="/plukspanne"
              element={
                <ProtectedRoute requiredPermissions={["spanne:visit"]}>
                  <MainLayout>
                    <SpanneDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plukspanne/skep"
              element={
                <ProtectedRoute requiredPermissions={["spanne:create"]}>
                  <MainLayout>
                    <SkepSpan />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plukspanne/wysig/:spanId"
              element={
                <ProtectedRoute requiredPermissions={["spanne:update"]}>
                  <MainLayout>
                    <WysigSpan />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plukspanne/:spanId"
              element={
                <ProtectedRoute requiredPermissions={["spanne:read"]}>
                  <MainLayout>
                    <ViewSpan />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/spanne"
              element={
                <ProtectedRoute requiredPermissions={["spanne:visit"]}>
                  <MainLayout>
                    <AktiwiteitSpanneDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/spanne/skep"
              element={
                <ProtectedRoute requiredPermissions={["spanne:create"]}>
                  <MainLayout>
                    <AktiwiteitSkepSpan />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/spanne/wysig/:spanId"
              element={
                <ProtectedRoute requiredPermissions={["spanne:update"]}>
                  <MainLayout>
                    <AktiwiteitWysigSpan />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/spanne/:spanId"
              element={
                <ProtectedRoute requiredPermissions={["spanne:read"]}>
                  <MainLayout>
                    <AktiwiteitViewSpan />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/vloot"
              element={
                <ProtectedRoute requiredPermissions={["voertuie:visit"]}>
                  <MainLayout>
                    <Voertuie />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vloot/skep"
              element={
                <ProtectedRoute requiredPermissions={["voertuie:create"]}>
                  <MainLayout>
                    <VoertuieSkep />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vloot/wysig/:rekordId"
              element={
                <ProtectedRoute requiredPermissions={["voertuie:update"]}>
                  <MainLayout>
                    <VoertuieWysig />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vloot/tipes"
              element={
                <ProtectedRoute requiredPermissions={["voertuie:visit"]}>
                  <MainLayout>
                    <Voertuigtipes />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vloot/tipes/skep"
              element={
                <ProtectedRoute requiredPermissions={["voertuie:create"]}>
                  <MainLayout>
                    <VoertuigtipeSkep />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vloot/tipes/wysig/:rekordId"
              element={
                <ProtectedRoute requiredPermissions={["voertuie:update"]}>
                  <MainLayout>
                    <VoertuigtipeWysig />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/toerusting"
              element={
                <ProtectedRoute requiredPermissions={["toerusting:visit"]}>
                  <MainLayout>
                    <Toerusting />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/toerusting/skep"
              element={
                <ProtectedRoute requiredPermissions={["toerusting:create"]}>
                  <MainLayout>
                    <ToerustingSkep />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/toerusting/wysig/:rekordId"
              element={
                <ProtectedRoute requiredPermissions={["toerusting:update"]}>
                  <MainLayout>
                    <ToerustingWysig />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/toerusting/tipes"
              element={
                <ProtectedRoute requiredPermissions={["toerusting:visit"]}>
                  <MainLayout>
                    <Toerustingtipes />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/toerusting/tipes/skep"
              element={
                <ProtectedRoute requiredPermissions={["toerusting:create"]}>
                  <MainLayout>
                    <ToerustingtipeSkep />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/toerusting/tipes/wysig/:rekordId"
              element={
                <ProtectedRoute requiredPermissions={["toerusting:update"]}>
                  <MainLayout>
                    <ToerustingtipeWysig />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/huuritems"
              element={
                <ProtectedRoute requiredPermissions={["huuritems:visit"]}>
                  <MainLayout>
                    <HuurItems />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/huuritems/skep"
              element={
                <ProtectedRoute requiredPermissions={["huuritems:create"]}>
                  <MainLayout>
                    <HuurItemsSkep />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/huuritems/wysig/:rekordId"
              element={
                <ProtectedRoute requiredPermissions={["huuritems:update"]}>
                  <MainLayout>
                    <HuurItemsWysig />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/plukstate"
              element={
                <ProtectedRoute requiredPermissions={["plukstate:read"]}>
                  <MainLayout>
                    <PlukstaatDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plukstate/skep"
              element={
                <ProtectedRoute requiredPermissions={["plukstate:create"]}>
                  <MainLayout>
                    <SkepPlukstaat />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plukstate/wysig/:plukstaatId"
              element={
                <ProtectedRoute requiredPermissions={["plukstate:update"]}>
                  <MainLayout>
                    <WysigPlukstaat />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plukstate/data-invoer/:plukstaatId"
              element={
                <ProtectedRoute requiredPermissions={["plukrekords:create"]}>
                  <MainLayout>
                    <PlukstaatInvoer />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plukstate/sien-rekords/:plukstaatId"
              element={
                <ProtectedRoute requiredPermissions={["plukrekords:read"]}>
                  <MainLayout>
                    <SienPlukstaatRekords />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plukstate/uitdruk/:plukstaatId"
              element={
                <ProtectedRoute requiredPermissions={["plukstate:read"]}>
                  <MainLayout>
                    <PlukstaatUitdruk />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plukstate/koppel-lewerings/:plukstaatId"
              element={
                <ProtectedRoute requiredPermissions={["plukstaatkoppelling:update"]}>
                  <MainLayout>
                    <PlukstaatKoppelings />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plukstate/bestuur-bins/:plukstaatId"
              element={
                <ProtectedRoute requiredPermissions={["pluk:rekordsverander"]}>
                  <MainLayout>
                    <BinsManagement />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plukstate/skuif-sakke/:plukstaatId"
              element={
                <ProtectedRoute requiredPermissions={["pluk:rekordsverander"]}>
                  <MainLayout>
                    <SakkeManagement />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pluk/plukprestasie/plukstate"
              element={
                <ProtectedRoute requiredPermissions={["pluk:rapportering"]}>
                  <MainLayout>
                    <Plukprestasie />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pluk/plukprestasie/verslag"
              element={
                <ProtectedRoute requiredPermissions={["pluk:rapportering"]}>
                  <MainLayout>
                    <PlukprestasieVerslag />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pluk"
              element={
                <ProtectedRoute anyPermissions={["pluk:rapportering", "plukstate:read"]}>
                  <MainLayout>
                    <PlukPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pluk/plukstate/uitstaande"
              element={
                <ProtectedRoute requiredPermissions={["plukstate:read"]}>
                  <MainLayout>
                    <UitstaandePlukstate />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pluk/insidente"
              element={
                <ProtectedRoute requiredPermissions={["insidente:read"]}>
                  <MainLayout>
                    <Insidente />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pluk/insidente/kategorie"
              element={
                <ProtectedRoute requiredPermissions={["insidente:read"]}>
                  <MainLayout>
                    <InsidenteKategorie />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pluk/insidente/kategorie/skep"
              element={
                <ProtectedRoute requiredPermissions={["insidente:create"]}>
                  <MainLayout>
                    <InsidenteKategorieSkep />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pluk/insidente/kategorie/wysig/:rekordId"
              element={
                <ProtectedRoute requiredPermissions={["insidente:update"]}>
                  <MainLayout>
                    <InsidenteKategorieWysig />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/finansieel"
              element={
                <ProtectedRoute anyPermissions={["lone:read", "ivj:read"]}>
                  <MainLayout>
                    <FinansielDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finansieel/lone"
              element={
                <ProtectedRoute requiredPermissions={["lone:read"]}>
                  <MainLayout>
                    <Lone />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finansieel/intervertakking-joernale"
              element={
                <ProtectedRoute requiredPermissions={["ivj:read"]}>
                  <MainLayout>
                    <IntervertakkingJoernale />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finansieel/kontrakteur-joernale"
              element={
                <ProtectedRoute requiredPermissions={["ivj:read"]}>
                  <MainLayout>
                    <KontrakteurJoernale />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/produksie"
              element={
                <ProtectedRoute anyPermissions={["oesskatting:visit"]}>
                  <MainLayout>
                    <Produksie />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/produksie/oesskatting"
              element={
                <ProtectedRoute requiredPermissions={["oesskatting:visit"]}>
                  <MainLayout>
                    <AnderSkattings />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/boerderye"
              element={
                <ProtectedRoute requiredPermissions={["boerderye:visit"]}>
                  <MainLayout>
                    <BoerderyManagement />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/boerderye/briefhoof/:rekordId"
              element={
                <ProtectedRoute requiredPermissions={["boerderye:visit"]}>
                  <MainLayout>
                    <BriefhoofBladsy />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/boerderye/skep"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <SkepBoerdery />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/seisoene"
              element={
                <ProtectedRoute requiredPermissions={["seisoene:visit"]}>
                  <MainLayout>
                    <Seisoene />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/seisoene/skep"
              element={
                <ProtectedRoute requiredPermissions={["seisoene:create"]}>
                  <MainLayout>
                    <SkepSeisoen />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/seisoene/wysig/:seisoenId"
              element={
                <ProtectedRoute requiredPermissions={["seisoene:update"]}>
                  <MainLayout>
                    <WysigSeisoen />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/gewasse"
              element={
                <ProtectedRoute requiredPermissions={["gewasse:visit"]}>
                  <MainLayout>
                    <Gewasse />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gewasse/skep"
              element={
                <ProtectedRoute requiredPermissions={["gewasse:create"]}>
                  <MainLayout>
                    <SkepGewas />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gewasse/wysig/:gewasId"
              element={
                <ProtectedRoute requiredPermissions={["gewasse:update"]}>
                  <MainLayout>
                    <WysigGewas />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/kultivars"
              element={
                <ProtectedRoute requiredPermissions={["kultivars:visit"]}>
                  <MainLayout>
                    <Kultivars />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/kultivars/skep"
              element={
                <ProtectedRoute requiredPermissions={["kultivars:create"]}>
                  <MainLayout>
                    <SkepKultivar />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/kultivars/wysig/:kultivarId"
              element={
                <ProtectedRoute requiredPermissions={["kultivars:update"]}>
                  <MainLayout>
                    <WysigKultivar />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/maatskappye"
              element={
                <ProtectedRoute requiredPermissions={["maatskappye:visit"]}>
                  <MainLayout>
                    <Maatskappye />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/maatskappye/skep"
              element={
                <ProtectedRoute requiredPermissions={["maatskappye:create"]}>
                  <MainLayout>
                    <SkepMaatskappy />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/maatskappye/wysig/:maatskappyId"
              element={
                <ProtectedRoute requiredPermissions={["maatskappye:update"]}>
                  <MainLayout>
                    <WysigMaatskappy />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/rapportering/teenwoordigheid"
              element={
                <ProtectedRoute requiredPermissions={["clocking:read"]}>
                  <MainLayout>
                    <TeenwoordigheidDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rapportering/teenwoordigheid/verslag"
              element={
                <ProtectedRoute requiredPermissions={["clocking:read"]}>
                  <MainLayout>
                    <TeenwoordigheidVerslag />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/teenwoordigheid"
              element={
                <ProtectedRoute requiredPermissions={["clocking:read"]}>
                  <MainLayout>
                    <TydregisterDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/teenwoordigheid/verslag"
              element={
                <ProtectedRoute requiredPermissions={["clocking:read"]}>
                  <MainLayout>
                    <TydregisterVerslag />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/rolle"
              element={
                <ProtectedRoute requiredPermissions={["rolle:visit"]}>
                  <MainLayout>
                    <Rolle />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rolle/skep"
              element={
                <ProtectedRoute requiredPermissions={["rolle:create"]}>
                  <MainLayout>
                    <SkepRolBladsy />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rolle/:rolId/sien"
              element={
                <ProtectedRoute requiredPermissions={["rolle:visit"]}>
                  <MainLayout>
                    <SienRol />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rolle/:rolId/wysig"
              element={
                <ProtectedRoute requiredPermissions={["rolle:update"]}>
                  <MainLayout>
                    <WysigRol />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/transaksies"
              element={
                <ProtectedRoute requiredPermissions={["transaksielog:read"]}>
                  <MainLayout>
                    <TransaksieLogs />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/profiel/:gebruikernaam/*"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Profiel />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/subskripsies"
              element={
                <ProtectedRoute requiredPermissions={["subskripsies:visit"]}>
                  <MainLayout>
                    <Subscriptions />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/subskripsies/skep"
              element={
                <ProtectedRoute requiredPermissions={["subskripsies:visit", "subskripsies:create"]}>
                  <MainLayout>
                    <SkepSubskripsie />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/subskripsies/:gebruikernaam/wysig"
              element={
                <ProtectedRoute requiredPermissions={["subskripsies:visit", "subskripsies:update"]}>
                  <MainLayout>
                    <SkepSubskripsie />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/subskripsies/:gebruikernaam/sien"
              element={
                <ProtectedRoute requiredPermissions={["subskripsies:visit"]}>
                  <MainLayout>
                    <SienSubskripsie />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/aktiwiteite/*"
              element={
                <ProtectedRoute requiredPermissions={["aktiwiteitemodule:toegang"]}>
                  <MainLayout>
                    <AktiwiteiteDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/vaardighede"
              element={
                <ProtectedRoute
                  anyPermissions={[
                    "vaardighede:create",
                    "vaardighede:update",
                    "vaardighede:delete",
                  ]}
                >
                  <MainLayout>
                    <Vaardighede />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vaardighede/skep"
              element={
                <ProtectedRoute requiredPermissions={["vaardighede:create"]}>
                  <MainLayout>
                    <SkepVaardigheid />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vaardighede/wysig/:vaardigheidId"
              element={
                <ProtectedRoute requiredPermissions={["vaardighede:update"]}>
                  <MainLayout>
                    <WysigVaardigheid />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vaardighede/:vaardigheidId"
              element={
                <ProtectedRoute requiredPermissions={["vaardighede:read"]}>
                  <MainLayout>
                    <ViewVaardigheid />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/modules"
              element={
                <ProtectedRoute requiredPermissions={["modules:read"]}>
                  <MainLayout>
                    <Modules />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/modules/:moduleId"
              element={
                <ProtectedRoute requiredPermissions={["modules:read"]}>
                  <MainLayout>
                    <ModuleInfo />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dienste"
              element={
                <ProtectedRoute requiredPermissions={["dienste:visit"]}>
                  <MainLayout>
                    <Dienste />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dienste/:diensverskafferid"
              element={
                <ProtectedRoute requiredPermissions={["dienste:visit"]}>
                  <MainLayout>
                    <DiensteInfo />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/verbruiksitems"
              element={
                <ProtectedRoute requiredPermissions={["verbruiksitem:visit"]}>
                  <MainLayout>
                    <Verbruiksitem />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/verbruiksitems/skep"
              element={
                <ProtectedRoute requiredPermissions={["verbruiksitem:create"]}>
                  <MainLayout>
                    <VerbruiksitemSkep />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/verbruiksitems/wysig/:rekordId"
              element={
                <ProtectedRoute requiredPermissions={["verbruiksitem:update"]}>
                  <MainLayout>
                    <VerbruiksitemWysig />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/verbruiksitems/tipes"
              element={
                <ProtectedRoute requiredPermissions={["verbruiksitem:visit"]}>
                  <MainLayout>
                    <Verbruiksitemtipes />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/verbruiksitems/tipes/skep"
              element={
                <ProtectedRoute requiredPermissions={["verbruiksitem:create"]}>
                  <MainLayout>
                    <VerbruiksitemtipeSkep />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/verbruiksitems/tipes/wysig/:rekordId"
              element={
                <ProtectedRoute requiredPermissions={["verbruiksitem:update"]}>
                  <MainLayout>
                    <VerbruiksitemtipeWysig />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/betaalmetodes"
              element={
                <ProtectedRoute requiredPermissions={["betaalmetode:read"]}>
                  <MainLayout>
                    <BetaalmetodesPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/betaalmetodes/skep"
              element={
                <ProtectedRoute requiredPermissions={["betaalmetode:create"]}>
                  <MainLayout>
                    <BetaalmetodesSetup />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/betaalmetodes/wysig/:id"
              element={
                <ProtectedRoute requiredPermissions={["betaalmetode:update"]}>
                  <MainLayout>
                    <BetaalmetodesSetup />
                  </MainLayout>
                </ProtectedRoute>
              }
            />


            <Route
              path="/opstellings/teenwoordigheid"
              element={
                <ProtectedRoute requiredPermissions={["tydsbeheeropstelling:read"]}>
                  <MainLayout>
                    <Klokopstellings />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/opstellings/teenwoordigheid/klokreels"
              element={
                <ProtectedRoute requiredPermissions={["tydsbeheeropstelling:read"]}>
                  <MainLayout>
                    <Klokreels />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/opstellings/teenwoordigheid/klokreels/skep"
              element={
                <ProtectedRoute requiredPermissions={["tydsbeheeropstelling:create"]}>
                  <MainLayout>
                    <KlokreelsSetup />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/opstellings/teenwoordigheid/klokreels/wysig/:id"
              element={
                <ProtectedRoute requiredPermissions={["tydsbeheeropstelling:update"]}>
                  <MainLayout>
                    <KlokreelsSetup />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/opstellings/teenwoordigheid/verlof"
              element={
                <ProtectedRoute requiredPermissions={["tydsbeheeropstelling:read"]}>
                  <MainLayout>
                    <Verlofreels />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/opstellings/teenwoordigheid/verlof/skep"
              element={
                <ProtectedRoute requiredPermissions={["tydsbeheeropstelling:create"]}>
                  <MainLayout>
                    <SkepVerlof />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/opstellings/teenwoordigheid/verlof/wysig/:id"
              element={
                <ProtectedRoute requiredPermissions={["tydsbeheeropstelling:update"]}>
                  <MainLayout>
                    <WysigVerlof />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/opstellings/teenwoordigheid/tvt"
              element={
                <ProtectedRoute requiredPermissions={["tydsbeheeropstelling:read"]}>
                  <MainLayout>
                    <TVTreels />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/opstellings/teenwoordigheid/tvt/skep"
              element={
                <ProtectedRoute requiredPermissions={["tydsbeheeropstelling:create"]}>
                  <MainLayout>
                    <SkepTvt />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/opstellings/teenwoordigheid/tvt/wysig/:id"
              element={
                <ProtectedRoute requiredPermissions={["tydsbeheeropstelling:update"]}>
                  <MainLayout>
                    <WysigTvt />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/pluk/algemeneopstellings"
              element={
                <ProtectedRoute requiredPermissions={["plukopstellings:update"]}>
                  <MainLayout>
                    <AlgemeneOpstellingsDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            >
              <Route path="*"
                element={
                  <ProtectedRoute requiredPermissions={["plukopstellings:update"]}>
                    <MainLayout>
                      <AlgemeneOpstellingsDashboard />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route
              path="/pluk/verslae"
              element={
                <ProtectedRoute requiredPermissions={["pluk:rapportering"]}>
                  <MainLayout>
                    <Plukverslae />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pluk/beplanner"
              element={
                <ProtectedRoute requiredPermissions={["pluk:rapportering"]}>
                  <MainLayout>
                    <Plukbeplanner />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/databronne"
              element={
                <ProtectedRoute requiredPermissions={["databronne:read"]}>
                  <MainLayout>
                    <DatabronneDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/databronne/skep"
              element={
                <ProtectedRoute requiredPermissions={["databronne:create"]}>
                  <MainLayout>
                    <DatabronneSkep />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/databronne/wysig/:rekordId"
              element={
                <ProtectedRoute requiredPermissions={["databronne:update"]}>
                  <MainLayout>
                    <DatabronneWysig />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/databronne/dupliseer/:rekordId/:ownerId"
              element={
                <ProtectedRoute requiredPermissions={["databronne:create"]}>
                  <MainLayout>
                    <DatabronneDupliseer />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/operationeel"
              element={
                <ProtectedRoute
                  anyPermissions={[
                    "werknemers:visit",
                  ]}
                >
                  <MainLayout>
                    <OperationeelDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/qrkodes"
              element={
                <ProtectedRoute requiredPermissions={["qrkodes:create"]}>
                  <MainLayout>
                    <QRKodes />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/opstellings/integrasies"
              element={
                <ProtectedRoute requiredPermissions={["integrasie:read"]}>
                  <MainLayout>
                    <IntegrasiesDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/opstellings/integrasies/payroll"
              element={
                <ProtectedRoute requiredPermissions={["integrasie:read"]}>
                  <MainLayout>
                    <PayrollIntegrasieDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/opstellings/integrasies/payroll/skep"
              element={
                <ProtectedRoute requiredPermissions={["integrasie:create"]}>
                  <MainLayout>
                    <PayrollIntegrasieSkep />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/opstellings/integrasies/payroll/wysig/:rekordId"
              element={
                <ProtectedRoute requiredPermissions={["integrasie:update"]}>
                  <MainLayout>
                    <PayrollIntegrasieWysig />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/rekening"
              element={
                <ProtectedRoute anyPermissions={["rekening:verbruik", "rekening:settings"]}>
                  <MainLayout>
                    <RekeningDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rekening/verstellings"
              element={
                <ProtectedRoute requiredPermissions={["rekening:verbruik"]}>
                  <MainLayout>
                    <RekeningVerstellings />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rekening/verbruik"
              element={
                <ProtectedRoute requiredPermissions={["rekening:settings"]}>
                  <MainLayout>
                    <VerbruikDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rekening/rekeninge"
              element={
                <ProtectedRoute requiredPermissions={["rekening:bills"]}>
                  <MainLayout>
                    <Rekeninge />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            {/*  
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredPermissions={["dashboard:visit"]}>
                  <MainLayout>
                    <QuicksightDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            /> */}
          </Route>
          <Route path="*" element={<NotFoundView />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundaryPage>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    addNotification: (data) => dispatch(actions.addNotification(data)),
    getNotifications: () => dispatch(actions.getNotifications()),
  };
};

export default connect(null, mapDispatchToProps)(RoutesComponent);
